import {
  type CSSProperties,
  type KeyboardEvent,
  useCallback,
  useId,
  useRef,
  useState,
} from 'react';

import { SERVICE_ORBIT, SERVICES } from '@config/services';
import { useInView } from '@hooks/useInView';

import { OrbitCore } from './OrbitCore';
import { OrbitItem } from './OrbitItem';
import styles from './ServiceOrbit.module.scss';

type StageStyle = CSSProperties & Record<'--count', string>;

/** Arrow keys step around the ring; Home and End jump to its ends. */
const KEY_STEP: Readonly<Record<string, number>> = {
  ArrowRight: 1,
  ArrowDown: 1,
  ArrowLeft: -1,
  ArrowUp: -1,
};

/**
 * The hero's service orbit: six services circling a glass core that
 * describes whichever one is selected.
 *
 * The orbit says what the company is: many disciplines around one team.
 * Motion is CSS only (one rotation for the ring, the same rotation
 * reversed on each pill so it stays upright), so React never renders per
 * frame. The ring stops whenever someone reaches for it: a button under
 * the pointer, keyboard focus inside the ring, or scrolled out of view.
 *
 * It keeps orbiting under reduced motion (`data-motion="essential"`), and
 * has no pause button, both at the owner's explicit request. The motion is
 * kept slow, linear and free of bounce or parallax. Note this does not
 * meet WCAG 2.2.2 (a way to pause motion lasting over five seconds); the
 * hover and focus pauses are the only stops.
 *
 * Semantics are a tab list: the pills are tabs, the core is their panel,
 * and focus moves around the ring with the arrow keys (one tab stop).
 */
export const ServiceOrbit = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const stageRef = useRef<HTMLDivElement>(null);
  const tablistRef = useRef<HTMLDivElement>(null);
  const inView = useInView(stageRef);
  const baseId = useId();

  const tabId = (index: number): string => `${baseId}-tab-${String(index)}`;
  const panelId = `${baseId}-panel`;
  const active = SERVICES[activeIndex] ?? SERVICES[0];

  const handleKeyDown = useCallback((event: KeyboardEvent<HTMLButtonElement>): void => {
    const count = SERVICES.length;
    let next: number | undefined;
    const step = KEY_STEP[event.key];
    if (step !== undefined)
      next = (Number(event.currentTarget.dataset.index) + step + count) % count;
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = count - 1;
    if (next === undefined) return;

    event.preventDefault();
    tablistRef.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]')[next]?.focus();
  }, []);

  if (!active) return null;

  const stageStyle: StageStyle = { '--count': String(SERVICES.length) };

  return (
    <div className={styles.orbit}>
      <div
        ref={stageRef}
        className={styles.stage}
        style={stageStyle}
        data-paused={!inView || undefined}
        data-motion="essential"
      >
        <div className={styles.glow} aria-hidden="true" />
        <div className={styles.track} aria-hidden="true" />

        <OrbitCore service={active} panelId={panelId} tabId={tabId(activeIndex)} />

        <div
          ref={tablistRef}
          className={styles.ring}
          role="tablist"
          aria-label={SERVICE_ORBIT.label}
        >
          {SERVICES.map((service, index) => (
            <OrbitItem
              key={service.id}
              service={service}
              index={index}
              isActive={index === activeIndex}
              panelId={panelId}
              tabId={tabId(index)}
              onSelect={setActiveIndex}
              onKeyDown={handleKeyDown}
            />
          ))}
        </div>
      </div>
    </div>
  );
};
